'use client'

import React from 'react'
import { Box, Link, Typography, Popover, List, ListItem, ListItemText, Divider, SvgIcon } from '@mui/material'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import { useLocale } from 'next-intl'

import BrandLogo from '../brandLogo'

interface MobileHeaderProps {
    user: { signedIn: boolean; email?: string }
    menuAnchor: HTMLElement | null
    onMenuOpen: (event: React.MouseEvent<HTMLElement>) => void
    onMenuClose: () => void
    onModalOpen: () => void
    onSignOut: () => void
    t: (key: string) => string
}

const MobileHeader: React.FC<MobileHeaderProps> = ({
    user,
    menuAnchor,
    onMenuOpen,
    onMenuClose,
    onModalOpen,
    onSignOut,
    t
}) => {
    const locale = useLocale()
    const isRTL = locale === 'ar'

    return (
        <>
            <Box
                sx={{
                    width: '100%',
                    bgcolor: 'background.paper',
                    borderBottom: '1px solid',
                    borderColor: 'grey.200',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 2,
                    py: 2,
                    height: 54,
                    direction: isRTL ? 'rtl' : 'ltr'
                }}
            >
                {/* Left: Logo */}
                <Box
                    sx={{
                        height: 26,
                        display: 'flex',
                        alignItems: 'center',
                        cursor: 'pointer'
                    }}
                    onClick={() => (window.location.href = '/')}
                >
                    <BrandLogo />
                </Box>

                {/* Right: Sign In / User Menu */}
                {user.signedIn ? (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            cursor: 'pointer',
                            pointerEvents: user.email ? 'auto' : 'none',
                            opacity: user.email ? 1 : 0.7
                        }}
                        onClick={e => user.email && onMenuOpen(e)}
                    >
                        <PersonOutlineIcon sx={{ fontWeight: 300, fontSize: 20, mr: 0.5 }} />
                        <Typography
                            sx={{
                                fontWeight: 500,
                                fontSize: 14,
                                maxWidth: 100,
                                textOverflow: 'ellipsis',
                                overflow: 'hidden',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {user.email?.split('@')[0] || 'User'}
                        </Typography>
                        {user.email && (
                            <SvgIcon sx={{ fontSize: 17, ml: 0.5 }}>
                                <svg width="17" height="17" viewBox="0 0 24 24">
                                    <path d="M7 10l5 5 5-5" stroke="#1a2a3a" strokeWidth="1.3" fill="none" />
                                </svg>
                            </SvgIcon>
                        )}
                    </Box>
                ) : (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            px: 1.5,
                            py: 0.75,
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: 'primary.main',
                            bgcolor: 'background.paper',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            '&:hover': {
                                bgcolor: 'primary.lighter',
                                borderColor: 'primary.dark'
                            }
                        }}
                        onClick={() => onModalOpen()}
                    >
                        <PersonOutlineIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                        <Typography
                            sx={{
                                fontWeight: 500,
                                fontSize: 14,
                                color: 'primary.main'
                            }}
                        >
                            {t('signIn')}
                        </Typography>
                    </Box>
                )}
            </Box>

            {/* User Menu Popover */}
            <Popover
                open={!!menuAnchor && user.signedIn}
                anchorEl={menuAnchor}
                onClose={onMenuClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: isRTL ? 'left' : 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: isRTL ? 'left' : 'right' }}
                PaperProps={{
                    sx: {
                        mt: 1,
                        borderRadius: 1.5,
                        minWidth: 185,
                        boxShadow: 3,
                        p: 0,
                        overflow: 'hidden',
                        background: '#fff'
                    }
                }}
            >
                <List sx={{ p: 0, maxHeight: 300, overflowY: 'auto' }}>
                    {[
                        { label: t('userMenu.myBookings'), href: '/accounts/trips' },
                        { label: t('userMenu.myWallet'), href: '/accounts/wallet' },
                        { label: t('userMenu.favouriteHotels'), href: '/savedHotels' },
                        { label: t('userMenu.myReviews'), href: '/manageReviews' },
                        { label: t('userMenu.changePassword'), href: '/changePassword' },
                        { label: t('userMenu.giveFeedback'), href: '/feedback' }
                    ].map(item => (
                        <ListItem
                            key={item.label}
                            component="a"
                            href={item.href}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.8,
                                py: 0.75,
                                px: 2,
                                minHeight: 40,
                                border: 0,
                                borderRadius: 0,
                                fontWeight: 500,
                                fontSize: 15,
                                color: '#1a2a3a',
                                background: '#fff',
                                textDecoration: 'none',
                                transition: 'background 0.2s',
                                '&:hover': {
                                    background: '#f3f4f6',
                                    textDecoration: 'none'
                                }
                            }}
                        >
                            <ListItemText
                                primary={item.label}
                                primaryTypographyProps={{
                                    fontSize: 15,
                                    fontWeight: 500,
                                    color: '#4b5563'
                                }}
                            />
                        </ListItem>
                    ))}
                    <Divider />
                    <ListItem
                        component="button"
                        onClick={onSignOut}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.8,
                            py: 0.5,
                            px: 2,
                            minHeight: 25,
                            border: 0,
                            borderRadius: 0,
                            fontWeight: 500,
                            fontSize: 15,
                            color: 'error.main',
                            background: '#fff',
                            transition: 'background 0.2s',
                            '&:hover': {
                                background: '#f3f4f6'
                            }
                        }}
                    >
                        <ListItemText
                            primary={t('userMenu.signOut')}
                            primaryTypographyProps={{ fontSize: 15, fontWeight: 500, color: 'error.main' }}
                        />
                    </ListItem>
                </List>
            </Popover>
        </>
    )
}

export { MobileHeader }
export type { MobileHeaderProps }
